import axios from "./axiosConfig";
import { STATUS_MAP } from "pages/enterpriseDashboard/utils";
import { cleanTitle, isUserAdmin } from "./userHelpers";

const EARNINGS_CALL_RE = /^Q[1-4]\s(201[0-9]|202[0-6])\sEarnings\sCall$/;

export const getWhitelistUsers = async (params) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: "/login/valid-users",
      params,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createWhitelistUser = async (userData) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: "/login/valid-users",
      data: userData,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateWhitelistUser = async (email, userData) => {
  try {
    const { data } = await axios({
      method: "PUT",
      url: `/login/valid-users/${email}`,
      data: userData,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteWhitelistUser = async (email) => {
  try {
    const { data } = await axios({
      method: "DELETE",
      url: `/login/valid-users/${email}`,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const raiseDispute = async (disputeData) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/user/dispute`,
      data: disputeData,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getAllLeaves = async (params = {}) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/leave`,
      params: {
        name: params.search,
        leave_type: params.leave_type?.join(","),
        status: params.status?.join(","),
        start_date: params.start_date,
        end_date: params.end_date,
      },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const requestLeave = async (leaveData) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/leave/apply`,
      data: leaveData,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getAllEvents = async (params = {}) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/enterprise/events`,
      params: {
        company_name: params.company_name,
        priority_score: params.priority_score?.join(","),
        date_from: params.date_from,
        date_to: params.date_to,
        source: params.source,
        speaker_name: params.speaker_name,
        search: params.search,
        sort_by: params.sort_by || "date",
        sort_order: params.sort_order || "desc",
        start: params.start || 0,
        limit: params.limit || 1000,
      },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const approveLeave = async (leaveId) => {
  try {
    const { data } = await axios({
      method: "PUT",
      url: `/leave/${leaveId}/approve`,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const rejectLeave = async (leaveId) => {
  try {
    const { data } = await axios({
      method: "PUT",
      url: `/leave/${leaveId}/reject`,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteLeave = async (leaveId) => {
  try {
    const { data } = await axios({
      method: "DELETE",
      url: `leave/${leaveId}`,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getEditors = async (params = {}) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/v1/user/all`,
      params,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getUserProfile = async (userId) => {
  try {
    const url = userId ? `/v1/user/profile/${userId}` : "/v1/user/profile";
    const { data } = await axios({
      method: "GET",
      url,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateUserField = async (field, value, userId = null) => {
  try {
    const payload = userId
      ? { [field]: value, user_id: userId }
      : { [field]: value };
    const { data } = await axios({
      method: "POST",
      url: `/v1/user/profile`,
      data: payload,
    });
    // Update localStorage with new data
    if (userId == null) {
      const userInfo = JSON.parse(localStorage.getItem("userInfo"));
      userInfo.user = { ...userInfo.user, [field]: value };
      localStorage.setItem("userInfo", JSON.stringify(userInfo));
    }
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

//Account Management apis
export const getAcceptedMembers = async ({ mini = true } = {}) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: "/enterprise/get_company_members/accepted",
      params: { mini },
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const copyWorkflow = async (id, numCopies, startIndex) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${id}/copy`,
      data: {
        num_copies: parseInt(numCopies),
        start_index: parseInt(startIndex),
      },
    });
    return data;
  } catch (error) {
    throw new Error(error.response.data.message);
  }
};

export const reprocessWorkflow = async (id, type) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${id}/reprocess`,
      data: { type },
    });
    return data;
  } catch (error) {
    throw new Error(error.response.data.message);
  }
};

export const autoAssignEditors = async (type = "ALL") => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/admin/trigger-auto-assign`,
      params: { type },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteWorkflow = async (id) => {
  try {
    const { data } = await axios({
      method: "DELETE",
      url: `/enterprise/workflow/${id}`,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateWorkflowData = async (id, { title, status }) => {
  try {
    if (title) {
      const { data } = await axios({
        method: "PUT",
        url: `/enterprise/workflow/${id}/rename`,
        data: { new_title: title },
      });
      return data;
    } else if (status) {
      const { data } = await axios({
        method: "POST",
        url: `/enterprise/workflow/${id}/workflow_status`,
        data: { workflow_status: status },
      });
      return data;
    }
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateDeliveryStatus = async (id, statusData) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${id}/delivery-status`,
      data: statusData,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const validateWorkflow = async (
  workflowId,
  validate = true,
  userId = null
) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${workflowId}/validate`,
      data: { validate, ...(userId && { user_id: userId }) },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

//workflow apis
export const getCompletedWorkflow = async ({
  pageParam,
  activeTab = "All Files",
  customFilter = {},
  globalFilter = "",
  sorting = [],
  fetchSize = 50,
}) => {
  try {
    const url = new URL("/enterprise/workflow", axios.defaults.baseURL);

    // Handle tab-specific base filters
    if (activeTab === "Unassigned") {
      url.searchParams.set("processing_status", "Completed");
      // Filter by assignee count (0 or 1) - this is always needed for Unassigned
      url.searchParams.set("max_assignees", "1");
    } else {
      url.searchParams.set(
        "processing_status",
        activeTab === "Incomplete" ? "Incomplete" : "Completed"
      );

      if (activeTab !== "All Files") {
        const workflowStatus = Object.keys(STATUS_MAP).find(
          (key) => STATUS_MAP[key] === activeTab
        );
        if (workflowStatus) {
          url.searchParams.set("workflow_status", workflowStatus);
        }
      }
    }

    if (activeTab === "All Files") {
      if ("status_arr" in customFilter) {
        const statusKeys = customFilter.status_arr
          .map((status) =>
            Object.keys(STATUS_MAP).find((key) => STATUS_MAP[key] === status)
          )
          .filter(Boolean); // Filter out any undefined values
        if (statusKeys.length) {
          url.searchParams.set("workflow_status", statusKeys.join(","));
        }
      }
    }
    url.searchParams.set("start", `${pageParam * fetchSize}`);
    url.searchParams.set("limit", `${fetchSize}`);

    // Don't set default upload_type - let it come from customFilter

    url.searchParams.set("text", globalFilter);
    if (sorting.length) {
      url.searchParams.set("sort_by", sorting[0].id);
      url.searchParams.set("sort_order", sorting[0].desc ? "desc" : "asc");
    }
    // Don't set any default date filters - let them come from customFilter only

    Object.entries(customFilter).forEach(([key, value]) => {
      if (key !== "status_arr") {
        // Handle arrays by joining with comma
        if (Array.isArray(value)) {
          url.searchParams.set(key, value.join(','));
        } else {
          url.searchParams.set(key, value);
        }
      }
    });
    const { data } = await axios({
      method: "GET",
      url: url.href,
    });

    if (data?.data) {
      const isAdmin = isUserAdmin();
      data.data.forEach(item => {
        if (!isAdmin) item.title = cleanTitle(item.title);
        if (item.company && EARNINGS_CALL_RE.test(item.title)) item.title += ` - ${item.company}`;
      });
    }

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getWorkflowTranscript = async (id, hideTranscript = false) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: "/enterprise/workflow/" + id,
      params: {
        hide_transcript: hideTranscript,
      }
    });

    if (data?.title) {
      if (!isUserAdmin()) data.title = cleanTitle(data.title);
      if (data.company && EARNINGS_CALL_RE.test(data.title)) data.title += ` - ${data.company}`;
    }

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getTranscriptVersion = async (id, version) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/enterprise/transcript/${id}`,
      params: {
        [version]: 1,
        start: 0,
        limit: 5000,
      }
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getPaginatedWorkflowTranscript = async (id, start = 25, limit = 5000) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/enterprise/transcript/${id}?start=${start}&limit=${limit}`,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const completeWorkflow = async (id) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${id}/workflow_status`,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const reviewWorkflow = async (id) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${id}/review`,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createWorkflow = async (payload) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: "/enterprise/workflow",
      data: payload,
      headers: { "Content-Type": "multipart/form-data" },
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateMultipleWorkflowStatuses = async (
  workflowIds,
  workflowStatus
) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: "/enterprise/workflows/update_status",
      data: {
        workflow_ids: workflowIds,
        workflow_status: workflowStatus,
      },
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const qaTakeoverWorkflows = async (workflowIds, takeOverValue) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: "/enterprise/workflow/qa_takeover",
      data: {
        workflow_ids: workflowIds,
        je_completion: takeOverValue,
      },
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const assignMemberToWorkflows = async (workflowIds, assignee) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: "/enterprise/workflow/assign_member",
      data: {
        workflow_ids: workflowIds,
        assignee: assignee,
      },
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const assignWorkflow = async (docid, payload) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${docid}/assignee`,
      data: payload,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const saveWorkflow = async (docid, payload) => {
  // const userId = getUserId()
  const isDeltaUpdates = process.env.REACT_APP_DELTA_UPDATES === "true";
  const url = isDeltaUpdates ? `/v3/enterprise/transcript/${docid}` : `/v2/enterprise/transcript/${docid}`;

  try {
    // rollbar.info(`Saving workflow ${docid} ${userId}`);
    const { data } = await axios({
      method: "POST",
      url,
      data: payload,
    });

    return data;
  } catch (e) {
    // rollbar.error(`Error saving workflow ${docid} ${userId}`, { error: e });
    return Promise.reject(e);
  }
};

export const removeAssignedWorkflow = async (docid, payload) => {
  try {
    const { data } = await axios({
      method: "DELETE",
      url: `/enterprise/workflow/${docid}/assignee`,
      data: payload,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateSpeakers = async (docid, payload) => {
  try {
    const { data } = await axios({
      method: "PUT",
      url: `/enterprise/workflow/${docid}/speakers`,
      data: payload,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateSpecificSpeaker = async (docid, itemid, payload) => {
  try {
    const { data } = await axios({
      method: "PUT",
      url: `/enterprise/transcript/${docid}/item/${itemid}/speaker`,
      data: payload,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const strikeText = async (docid, itemid, payload) => {
  try {
    const { data } = await axios({
      method: "PUT",
      url: `/enterprise/transcript/${docid}/item/${itemid}/strikethrough`,
      data: payload,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteStrikedText = async (docid, itemid, payload) => {
  try {
    const { data } = await axios({
      method: "DELETE",
      url: `/enterprise/transcript/${docid}/item/${itemid}/strikethrough`,
      data: payload,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const findReplace = async (docid, payload) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/transcript/find_and_replace/${docid}`,
      data: payload,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const addComment = async (docid, itemId, payload) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/comments/${docid}/transcript/${itemId}`,
      data: payload,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateComment = async ({ docid, itemId, body }) => {
  try {
    const { data } = await axios({
      method: "PUT",
      url: `/enterprise/comments/${docid}/transcript/${itemId}/comment/${body._id}`,
      data: body,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteComment = async (docid, itemId, commentId) => {
  try {
    const { data } = await axios({
      method: "DELETE",
      url: `/enterprise/comments/${docid}/transcript/${itemId}/comment/${commentId}`,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const exportTranscript = async (docid, format, options) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/enterprise/transcript/export/${docid}/${format}`,
      responseType: "blob",
      params: options,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const recalculateScore = async (id) => {
  const url = "/enterprise/hiring/score/recalculate";
  try {
    const { data } = await axios({
      method: "POST",
      data: { ids: id },
      url,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getTestFeedback = async (id) => {
  const url = `/enterprise/hiring/score/${id}`;
  try {
    const { data } = await axios({
      method: "GET",
      url,
    });

    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const downloadAllZipFile = async () => {
  try {
    const url = `/enterprise/hiring/score/export_report_zip/all`;
    const response = await axios({
      method: "POST",
      url,
      data: {},
      responseType: "blob",
    });
    return response.data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getEditorAnalytics = async () => {
  try {
    const { data } = await axios({
      method: "GET",
      url: "/enterprise/analytics/editors",
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

// ─── Billing APIs ──────────────────────────────────────────────────

export const getBillingCycles = async () => {
  try {
    const { data } = await axios({
      method: "GET",
      url: "/enterprise/billing/cycles",
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const exportWiseCsvs = async (fromDate, toDate, userIds = []) => {
  try {
    const params = { from_date: fromDate, to_date: toDate };
    if (Array.isArray(userIds) && userIds.length) {
      params.user_ids = userIds.join(",");
    }
    const response = await axios({
      method: "GET",
      url: "/enterprise/billing/export/wise",
      params,
      responseType: "blob",
    });
    return response.data;
  } catch (e) {
    if (e?.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        if (text) {
          try {
            const parsed = JSON.parse(text);
            const msg = parsed?.error || parsed?.message || text;
            return Promise.reject(new Error(msg));
          } catch {
            return Promise.reject(new Error(text));
          }
        }
      } catch {
        // Fall through to default rejection
      }
    }
    return Promise.reject(e);
  }
};

export const exportDetailedCsv = async (fromDate, toDate) => {
  try {
    const response = await axios({
      method: "GET",
      url: "/enterprise/billing/export/detailed",
      params: { from_date: fromDate, to_date: toDate },
      responseType: "blob",
    });
    return response.data;
  } catch (e) {
    if (e?.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        if (text) {
          try {
            const parsed = JSON.parse(text);
            const msg = parsed?.error || parsed?.message || text;
            return Promise.reject(new Error(msg));
          } catch {
            return Promise.reject(new Error(text));
          }
        }
      } catch {
        // Fall through to default rejection
      }
    }
    return Promise.reject(e);
  }
};

export const getEditorsBilling = async (fromDate, toDate) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: "/enterprise/billing/editors",
      params: { from_date: fromDate, to_date: toDate },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getEditorBilling = async (userId, fromDate, toDate) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/enterprise/billing/editor/${userId}`,
      params: { from_date: fromDate, to_date: toDate },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const calculateBilling = async (fromDate, toDate) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: "/enterprise/billing/calculate",
      data: { from_date: fromDate, to_date: toDate },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const overrideBilling = async (billingId, overrideData) => {
  try {
    const { data } = await axios({
      method: "PATCH",
      url: `/enterprise/billing/${billingId}/override`,
      data: overrideData,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createBillingAdjustment = async (adjustmentData) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: "/enterprise/billing/adjustment",
      data: adjustmentData,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const validateBillingRecord = async (billingId, validate = true, userId = null) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/billing/${billingId}/validate`,
      data: { validate, ...(userId && { user_id: userId }) },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getEditorAdjustments = async (userId, fromDate, toDate) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/enterprise/billing/adjustments/${userId}`,
      params: { from_date: fromDate, to_date: toDate },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getAllInvoices = async (cycleLabel) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: "/enterprise/billing/invoices",
      params: cycleLabel ? { cycle_label: cycleLabel } : {},
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getEditorInvoice = async (userId, fromDate, toDate, options = {}) => {
  try {
    const params = { from_date: fromDate, to_date: toDate };
    if (options?.mode) params.mode = options.mode;
    if (typeof options?.refresh === "boolean") params.refresh = options.refresh;
    const { data } = await axios({
      method: "GET",
      url: `/enterprise/billing/editor/${userId}/invoice`,
      params,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const sendEditorInvoice = async (userId, fromDate, toDate) => {
  try {
    const response = await axios({
      method: "POST",
      url: `/enterprise/billing/editor/${userId}/invoice/send`,
      data: { from_date: fromDate, to_date: toDate },
    });
    if (response.status === 207) {
      return { ...response.data, partial: true };
    }
    return response.data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getEditorInvoiceHistory = async (userId, refresh = false) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/enterprise/billing/editor/${userId}/invoice-history`,
      params: refresh ? { refresh: true } : {},
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getAllInvoiceHistory = async (params = {}) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: "/enterprise/billing/invoice-history",
      params,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getAllIncidents = async (filters = {}) => {
  try {
    // Build query parameters from filters
    const params = {};

    if (
      filters.status &&
      Array.isArray(filters.status) &&
      filters.status.length > 0
    ) {
      // Join multiple status values with comma
      params.status = filters.status.join(",");
    }

    if (filters.client_name && filters.client_name.trim()) {
      params.client_name = filters.client_name.trim();
    }

    const { data } = await axios({
      method: "GET",
      url: `/issues`,
      params: params,
    });

    return data;
  } catch (e) {
    console.error("Error fetching incidents:", e.response?.data || e.message);
    return Promise.reject(e);
  }
};

export const resolveIncident = async (incidentId, resolutionNotes = "") => {
  try {
    const { data } = await axios({
      method: "PUT",
      url: `/issues/${incidentId}/resolve`,
      data: { resolution_notes: resolutionNotes },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getChats = async (id) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/enterprise/workflow/${id}/chats`,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const addChats = async (id, payload) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${id}/chats`,
      data: payload,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const validateTranscriptEntities = async (workflowId) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${workflowId}/validate_transcript`,
      params: {
        speaker_attribution: "false", // disable speaker attribution validation by default
      }
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const triggerSpeakerAttributionCheck = async (workflowId) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${workflowId}/speaker_attribution`,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

// Validate speakers against authoritative sources
// Expects speakersCsv in the form: "First Last [Position] (Company), First Last [Position] (Company)"
export const validateSpeakers = async (workflowId, speakersCsv) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/validate-speakers`,
      data: {
        workflow_id: workflowId,
        speakers_csv: speakersCsv,
      },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const submitValidationFeedback = async (
  workflowId,
  feedbackData,
  reason,
  details
) => {
  try {
    const payload = {
      category: feedbackData.category || "",
      entity: feedbackData.entity || "",
      issues: feedbackData.issues,
      correct_form: feedbackData.correctForm || "",
      start_time: feedbackData.startTime || 0,
      is_correct: feedbackData.is_correct || false,
      comment: details ? `${reason}: ${details}` : reason,
    };
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/workflow/${workflowId}/validation_feedback`,
      data: payload,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const acceptAssignment = async (assignmentId) => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/assignment/${assignmentId}/accept`,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const rejectAssignment = async (assignmentId, comment = "") => {
  try {
    const { data } = await axios({
      method: "POST",
      url: `/enterprise/assignment/${assignmentId}/reject`,
      data: { comment },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getOnlineStatus = async (mini = true) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `/v1/user/online-status`,
      params: { mini },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const importUsers = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await axios({
      method: "POST",
      url: "/enterprise/import/users",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

export const exportUsersCsv = async () => {
  try {
    const response = await axios({
      method: "GET",
      url: "/enterprise/export/users",
      responseType: "blob",
    });
    return response.data;
  } catch (e) {
    if (e?.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        if (text) {
          try {
            const parsed = JSON.parse(text);
            const msg = parsed?.error || parsed?.message || text;
            return Promise.reject(new Error(msg));
          } catch {
            return Promise.reject(new Error(text));
          }
        }
      } catch {
        // Fall through to default rejection
      }
    }
    return Promise.reject(e);
  }
};

export const sendCoverageWarningNotification = async (coverageData) => {
  const totalPlayed = (coverageData.playedSegments || []).reduce(
    (sum, [start, end]) => sum + (end - start), 0
  );
  const viewedCount = Math.min(coverageData.viewedSnippetIds.length, coverageData.totalSnippetCount);

  const audioPercent = coverageData.audioDuration > 0
    ? parseFloat(((totalPlayed / coverageData.audioDuration) * 100).toFixed(1))
    : 0;
  const scrollPercent = coverageData.totalSnippetCount > 0
    ? parseFloat(((viewedCount / coverageData.totalSnippetCount) * 100).toFixed(1))
    : 0;

  const payload = {
    workflow_id: coverageData.workflowId,
    workflow_title: coverageData.workflowTitle || null,
    workflow_status: coverageData.workflowStatus || null,
    upload_type: coverageData.uploadType || null,
    user_name: coverageData.userName,
    user_email: coverageData.userEmail,
    org_role: coverageData.orgRole,
    audio: {
      duration_seconds: Math.round(coverageData.audioDuration || 0),
      played_seconds: Math.round(totalPlayed),
      played_percent: audioPercent,
      segment_count: coverageData.playedSegments.length,
      segments: coverageData.playedSegments.map(([s, e]) => [
        Math.round(s), Math.round(e),
      ]),
    },
    transcript: {
      total_snippets: coverageData.totalSnippetCount,
      viewed_snippets: viewedCount,
      viewed_percent: scrollPercent,
      viewed_snippet_ids: coverageData.viewedSnippetIds,
    },
    sessions: {
      count: coverageData.sessionCount || 1,
      first_opened_at: coverageData.editorOpenedAt || null,
      last_session_at: coverageData.lastSessionAt || null,
      submitted_at: new Date().toISOString(),
    },
    edit_count: coverageData.editCount || 0,
    duration_seconds: coverageData.durationSeconds || 0,
    job_type: coverageData.jobType || null,
    partner_name: coverageData.partnerName || null,
    team: coverageData.team || null,
    integrity_check: coverageData.integrity_check,
  };

  try {
    const { data } = await axios({
      method: "POST",
      url: "/coverage",
      data: payload,
    });
    return data;
  } catch (e) {
    return Promise.reject(e);
  }
};

